'use client';
import * as React from 'react';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCart } from '@/hooks/useCart';
import { formatPrice } from '@/lib/format';

const emptyForm = { customerName: '', customerEmail: '', customerPhone: '', notes: '' };

/**
 * Reservation cart: a header icon (with a live count badge) that opens a drawer
 * listing the chosen products and a short form to submit them as a reservation
 * (POST /api/orders). Prices are re-checked server-side on submit.
 */
export default function Cart() {
  const { items, count, totalCents, setQuantity, remove, clear } = useCart();
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState(emptyForm);
  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'done'>('idle');
  const [error, setError] = React.useState<string | null>(null);

  const currency = items[0]?.currency;

  function close() {
    setOpen(false);
    // Reset the confirmation once the drawer is dismissed.
    if (status === 'done') {
      setStatus('idle');
      setForm(emptyForm);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setStatus('submitting');
    const res = await fetch('/api/orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...form,
        items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
      }),
    });
    if (res.ok) {
      setStatus('done');
      clear();
    } else {
      const body = await res.json().catch(() => ({}));
      setError(body.error ?? 'Une erreur est survenue. Veuillez réessayer.');
      setStatus('idle');
    }
  }

  return (
    <>
      <IconButton onClick={() => setOpen(true)} aria-label="Ouvrir le panier" color="inherit">
        <Badge badgeContent={count} color="primary">
          <ShoppingCartIcon />
        </Badge>
      </IconButton>

      <Drawer anchor="right" open={open} onClose={close}>
        <Box sx={{ width: { xs: '100vw', sm: 400 }, maxWidth: '100vw', p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Typography variant="h6" gutterBottom>Votre réservation</Typography>

          {status === 'done' ? (
            <Alert severity="success">
              Merci ! Votre demande de réservation a bien été envoyée. Nous vous
              recontacterons pour la confirmer.
            </Alert>
          ) : items.length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              Votre panier est vide.
            </Typography>
          ) : (
            <>
              <Stack spacing={1.5} sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
                {items.map((item) => (
                  <Box key={item.productId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar variant="rounded" src={item.image} alt={item.name} sx={{ width: 48, height: 48 }} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatPrice(item.priceCents, item.currency)}
                      </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <IconButton size="small" aria-label="Diminuer la quantité" onClick={() => setQuantity(item.productId, item.quantity - 1)}>
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>{item.quantity}</Typography>
                      <IconButton size="small" aria-label="Augmenter la quantité" onClick={() => setQuantity(item.productId, item.quantity + 1)}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" aria-label="Retirer" onClick={() => remove(item.productId)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1">Total</Typography>
                <Typography variant="subtitle1" fontWeight={700}>{formatPrice(totalCents, currency)}</Typography>
              </Box>

              <Box component="form" onSubmit={submit}>
                <Stack spacing={2}>
                  <TextField label="Votre nom" size="small" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} required />
                  <TextField label="E-mail" type="email" size="small" value={form.customerEmail} onChange={(e) => setForm({ ...form, customerEmail: e.target.value })} required />
                  <TextField label="Téléphone" size="small" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })} required />
                  <TextField label="Remarques (facultatif)" size="small" multiline rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

                  {error && <Alert severity="error">{error}</Alert>}

                  <Button type="submit" variant="contained" size="large" disabled={status === 'submitting'}>
                    {status === 'submitting' ? 'Envoi…' : 'Demander la réservation'}
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
}
